import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

@Component({
  selector: 'stretchy-input',
  templateUrl: './stretchy-input.component.html',
  styleUrls: ['./stretchy-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StretchyInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class StretchyInputComponent
  implements OnChanges, AfterViewInit, ControlValueAccessor
{
  public isEditingInput = false;
  public defaultInputValue: string;

  @Input({ required: true }) value: string;
  @Output() valueChange = new EventEmitter<string>();

  @Input({ required: true }) ariaLabel: string;

  private onChange = (value: string) => {};
  private onTouched = () => {};
  inputControl = new FormControl('');

  constructor(private elementRef: ElementRef) {}

  _getStretchyElement() {
    return this.elementRef.nativeElement.querySelector(
      'h1.input-stretchy',
    ) as HTMLHeadingElement;
  }

  ngAfterViewInit(): void {
    this.inputControl.disable();
  }

  ngOnChanges(changes: SimpleChanges) {
    // resize input if model was externally modified/updated
    if (changes && !this.isEditingInput) {
      this.defaultInputValue = this.value;
      this.inputControl.setValue(this.value);
      this.inputControl.disable();
    }
  }

  _setFocus(elem: HTMLElement) {
    if (!elem) return;
    elem.focus();
    // For keyboard interaction, we need to set the cursor position
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(elem);
    range.collapse(false); // Collapse to end
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  setEditable() {
    if (this.isEditingInput) {
      return;
    }

    let stretchyInputElement = this._getStretchyElement();
    setTimeout(this._setFocus.bind(this, stretchyInputElement));
    this.inputControl.enable();
    this.isEditingInput = true;
  }

  _setNonEditable() {
    if (!this.isEditingInput) {
      return;
    }
    let nameVal = this.inputControl.value.replace(/\s/g, '');

    if (!nameVal) {
      // user deleted entire name, set the name and width values to the defaults
      this.value = this.defaultInputValue;
      this.inputControl.setValue(this.value);
    } else {
      this.value = this.inputControl.value;
    }
    this.valueChange.emit(this.value);
    this.onChange(this.value);
    this.isEditingInput = false;
    this.onTouched();
    this.inputControl.disable();
  }

  onInputBlur() {
    this._setNonEditable();
  }

  inputKeyDown(keyEvent: KeyboardEvent) {
    // handle enter key
    if (keyEvent.key === 'Enter' && !this.isEditingInput) {
      this.setEditable();
      keyEvent.preventDefault();
    } else if (keyEvent.key === 'Enter' && this.isEditingInput) {
      this._setNonEditable();
      keyEvent.preventDefault();
    }
  }

  writeValue(value: string): void {
    const next = value ?? '';
    this.value = next;
    this.defaultInputValue = next;
    this.inputControl.setValue(next, { emitEvent: false });
    this.inputControl.disable();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.inputControl[isDisabled ? 'disable' : 'enable']();
  }
}
